<script>
    import { BarLoader } from 'svelte-loading-spinners';
    import { navigate } from 'svelte-routing';
    import { currentUser } from '../store/store.js';
    import { handleAnalyzeBrf, handleCreateBrfExpert, analyzeUploadedStadgar, analyzeUploadedEnergy, analyzeUploadedFinancialPlan } from '../utils/analyze.js';
    import { v4 as uuidv4 } from 'uuid';
    import { createEventDispatcher } from 'svelte';

    const dispatch = createEventDispatcher();

    export let uploadType = 'årsredovisning';
    export let associatedCoreId = null;
    export let textsConfig = {};

    let fileInput = null;
    let completedCoreId = null;

    let step1Loading = false;
    let step1Done = false;
    let step2Loading = false;
    let step2Done = false;
    let step3Loading = false;
    let step3Done = false;

    let displayTexts;

    $: {
        let defaults;
        if (uploadType === 'stadgar') {
            defaults = {
                title: 'Ladda upp Stadgar',
                instruction: 'Klicka på knappen nedan för att välja stadgar (PDF)',
                step1Text: 'Laddar upp stadgar',
                step2Text: 'Analyserar stadgar',
                step3Text: 'Skapar expert på stadgar',
                buttonText: 'Välj fil (Stadgar)',
                infoText: 'Processen kan ta ca 1 minut.'
            };
        } else if (uploadType === 'energy') {
            defaults = {
                title: 'Ladda upp Energideklaration',
                instruction: 'Klicka på knappen nedan för att välja energideklaration (PDF).',
                step1Text: 'Laddar upp energideklaration',
                step2Text: 'Analyserar energideklaration',
                step3Text: 'Slutför bearbetning', // Step 3 label for energy
                buttonText: 'Välj fil (Energideklaration)',
                infoText: 'Analys kan ta ca 30 sekunder.'
            };
        } else if (uploadType === 'financial_plan') {
            defaults = {
                title: 'Ladda upp Ekonomisk Plan',
                instruction: 'Klicka på knappen nedan för att välja ekonomisk plan (PDF).',
                step1Text: 'Laddar upp ekonomisk plan',
                step2Text: 'Analyserar ekonomisk plan',
                step3Text: 'Slutför bearbetning', // Step 3 label for financial_plan
                buttonText: 'Välj fil (Ekonomisk Plan)',
                infoText: 'Analys kan ta ca 30 sekunder.'
            };
        } else { // Default to årsredovisning
            defaults = {
                title: 'Ladda upp Årsredovisning',
                instruction: 'Klicka på knappen nedan för att välja en PDF-fil.',
                step1Text: 'Laddar upp fil',
                step2Text: 'Analyserar förening',
                step3Text: 'Skapar expert på förening',
                buttonText: 'Välj fil',
                infoText: 'Analys kan ta ca 30 sekunder.'
            };
        }
        displayTexts = { ...defaults, ...textsConfig };
    }
    
    function resetStates() {
        step1Loading = false; step1Done = false;
        step2Loading = false; step2Done = false;
        step3Loading = false; step3Done = false;
        completedCoreId = null;
        if (fileInput) fileInput.value = '';
    }

    function openBrfPage() {
        if (completedCoreId) navigate('/brf/' + completedCoreId);
    }

    function triggerFileInput() {
        resetStates(); // Reset states when triggering a new upload
        fileInput.click();
    }

    async function handleFileUpload(event) {
        const file = event.target.files[0];
        if (!file) return;

        step1Loading = true; step1Done = false;
        dispatch('progress', { stage: 'upload', type: uploadType, loading: true, done: false });

        try {
            validateFile(file);
            const fileNameInBucket = await uploadFileToBucket(file);
            
            step1Loading = false; step1Done = true;
            dispatch('progress', { stage: 'upload', type: uploadType, loading: false, done: true, fileName: fileNameInBucket });

            await handlePostUploadActions(fileNameInBucket);

        } catch (error) {
            console.error(`Error in ${uploadType} file upload process:`, error.message);
            dispatch('error', { type: uploadType, message: error.message });
            step1Loading = false; step1Done = false; // Reset this step on error
             // Ensure other steps are also reset visually if they were triggered
            step2Loading = false; step2Done = false;
            step3Loading = false; step3Done = false;
            dispatch('progress', { stage: 'upload', type: uploadType, loading: false, done: false, error: true });
        }
    }

    async function uploadFileToBucket(file) {
        const bucketName = 'brf-redovisningar-to-analyze';
        const uniqueFileNameBase = uuidv4();
        let fileNamePrefix = '';
        if (uploadType === 'stadgar') {
            fileNamePrefix = 'stadgar_';
        } else if (uploadType === 'energy') {
            fileNamePrefix = 'energy_'; // Prefix for energy files
        } else if (uploadType === 'financial_plan') {
            fileNamePrefix = 'financial_plan_'; // Prefix for financial plan files
        }
        else {
            fileNamePrefix = 'redovisning_';
        }
        const fileName = `${fileNamePrefix}${uniqueFileNameBase}`;
        const uploadUrl = `https://storage.googleapis.com/upload/storage/v1/b/${bucketName}/o?uploadType=media&name=${encodeURIComponent(fileName)}`;

        const response = await fetch(uploadUrl, {
            method: 'POST',
            headers: { 'Content-Type': file.type },
            body: file
        });

        if (!response.ok) throw new Error('Failed to upload file to bucket');
        
        console.log(`${uploadType} file uploaded successfully to bucket as:`, fileName);
        return fileName;
    }

    async function handlePostUploadActions(fileNameInBucket) {
        step2Loading = true; step2Done = false;
        dispatch('progress', { stage: 'analysis', type: uploadType, loading: true, done: false });

        const isExpertStepUsed = uploadType === 'årsredovisning' || uploadType === 'stadgar';
        if (isExpertStepUsed) {
            step3Loading = true; step3Done = false;
            dispatch('progress', { stage: 'expert', type: uploadType, loading: true, done: false });
        }

        try {
            let current_core_id = associatedCoreId;

            if (uploadType === 'årsredovisning') {
                const newCoreId = await handleAnalyzeBrf(fileNameInBucket, current_core_id);
                if (newCoreId) current_core_id = newCoreId;
                step2Loading = false; step2Done = true;
                completedCoreId = current_core_id;
                dispatch('progress', { stage: 'analysis', type: uploadType, loading: false, done: true });

                step3Loading = false; step3Done = true;
                dispatch('progress', { stage: 'expert', type: uploadType, loading: false, done: true });
                handleCreateBrfExpert(fileNameInBucket, current_core_id)
                    .catch((err) => console.error('Background expert creation failed:', err.message));

            } else if (uploadType === 'stadgar') {
                if (!current_core_id) throw new Error('Associated Core ID is required for stadgar processing.');
                await analyzeUploadedStadgar(fileNameInBucket, current_core_id);
                step2Loading = false; step2Done = true;
                completedCoreId = current_core_id;
                dispatch('progress', { stage: 'analysis', type: uploadType, loading: false, done: true });

                step3Loading = false; step3Done = true;
                dispatch('progress', { stage: 'expert', type: uploadType, loading: false, done: true });
                handleCreateBrfExpert(fileNameInBucket, current_core_id)
                    .catch((err) => console.error('Background expert creation failed:', err.message));

            } else if (uploadType === 'energy') {
                if (!current_core_id) throw new Error('Associated Core ID is required for energy declaration processing.');
                await analyzeUploadedEnergy(fileNameInBucket, current_core_id);
                step2Loading = false; step2Done = true;
                completedCoreId = current_core_id;
                dispatch('progress', { stage: 'analysis', type: uploadType, loading: false, done: true });

            } else if (uploadType === 'financial_plan') {
                if (!current_core_id) throw new Error('Associated Core ID is required for financial plan processing.');
                await analyzeUploadedFinancialPlan(fileNameInBucket, current_core_id);
                step2Loading = false; step2Done = true;
                completedCoreId = current_core_id;
                dispatch('progress', { stage: 'analysis', type: uploadType, loading: false, done: true });
            }

            dispatch('complete', { type: uploadType, fileName: fileNameInBucket, coreId: current_core_id });

        } catch (error) {
            console.error(`Error in post-upload actions for ${uploadType}:`, error.message);
            dispatch('error', { type: uploadType, message: error.message, stage: step2Loading ? 'analysis' : 'unknown' });
            step2Loading = false; step2Done = false;
            step3Loading = false; step3Done = false;
            completedCoreId = null;
            dispatch('progress', { stage: 'analysis', type: uploadType, loading: false, done: false, error: true });
            dispatch('progress', { stage: 'expert', type: uploadType, loading: false, done: false, error: true });
        }
    }

    function validateFile(file) {
        if (!file || file.name.split('.').pop().toLowerCase() !== 'pdf') {
            throw new Error(`Uppladdad fil är ingen PDF. Filnamnet är: ${file?.name || 'okänt'}`);
        }
    }
</script>

{#if ($currentUser?.numberOfAnalysis < 10 || $currentUser?.type == "admin") || ((uploadType === 'stadgar' || uploadType === 'energy') && $currentUser) }
<div class="upload-container">
    <div class="header">
        <i class="fa-solid fa-file-arrow-up upload-icon"></i>
        <h2>{displayTexts.title}</h2>
    </div>
    <div class="upload-card">
        {#if step1Loading || step2Loading || step3Loading || step1Done || completedCoreId}
        <ul class="upload-steps">
            <li class="step">
                <span class="step-label">{displayTexts.step1Text}</span>
                <span class="step-status">
                    {#if step1Done}<i class="fa-solid fa-circle-check step-icon step-done" aria-hidden="true"></i>{:else if step1Loading}<BarLoader size="32" color="#FF3E00" unit="px" duration="1s"/>{/if}
                </span>
            </li>
            <li class="step">
                <span class="step-label">{displayTexts.step2Text}</span>
                <span class="step-status">
                    {#if step2Done}<i class="fa-solid fa-circle-check step-icon step-done" aria-hidden="true"></i>{:else if step2Loading}<BarLoader size="32" color="#FF3E00" unit="px" duration="1s"/>{/if}
                </span>
            </li>
            {#if uploadType === 'årsredovisning' || uploadType === 'stadgar'}
            <li class="step">
                <span class="step-label">{displayTexts.step3Text}</span>
                <span class="step-status">
                    {#if step3Done && !step3Loading}
                        <span class="step3-background"><i class="fa-solid fa-clock step-icon step-pending" aria-hidden="true"></i> Skapas i bakgrunden</span>
                    {:else if step3Loading}
                        <BarLoader size="32" color="#FF3E00" unit="px" duration="1s"/>
                    {/if}
                </span>
            </li>
            {/if}
        </ul>
        <div class="info-text">{displayTexts.infoText}</div>
        {#if completedCoreId}
           <button
            type="button"
            class="action-button open-brf-button"
            on:click={openBrfPage}
            on:keydown={(e) => e.key === 'Enter' && openBrfPage()}
           >
            <i class="fa-solid fa-external-link icon-prefix"></i> Öppna föreningens sida
           </button>
        {/if}
        {#if (step1Done && !step1Loading && !step2Loading && !step3Loading) || (!step1Loading && !step2Loading && !step3Loading && !step1Done && !fileInput?.files?.length)}
           <span
            class="action-button upload-trigger"
            on:click={triggerFileInput}
            on:keydown={(e) => e.key === 'Enter' && triggerFileInput()}
            tabindex="0"
            role="button"
            aria-label="Ladda upp en till fil"
           >
            <i class="fa-solid fa-upload icon-prefix"></i> {displayTexts.buttonText.replace("Välj", "Ladda upp ny")}
           </span>
        {/if}
        {:else}
        <p class="upload-instructions">{displayTexts.instruction}</p>
        <input type="file" bind:this={fileInput} on:change={handleFileUpload} accept=".pdf" class="styled-file-input" />
        <span
            class="action-button upload-trigger"
            on:click={triggerFileInput}
            on:keydown={(e) => e.key === 'Enter' && triggerFileInput()}
            tabindex="0"
            role="button"
            aria-label={displayTexts.buttonText}
        >
            <i class="fa-solid fa-upload icon-prefix"></i> {displayTexts.buttonText}
        </span>
        <div class="info-text">
            {#if uploadType === 'årsredovisning'}
            Kontrollera så att din BRF inte redan finns i systemet. Om du har problem, kontakta <a href="mailto:info@boanalys.io">info@boanalys.io</a>
            {:else if uploadType === 'stadgar'}
            Se till att du laddar upp föreningens stadgar i PDF-format.
            {:else if uploadType === 'energy'}
            Se till att du laddar upp föreningens energideklaration i PDF-format.
            {:else if uploadType === 'financial_plan'}
            Se till att du laddar upp föreningens ekonomiska plan i PDF-format.
            {/if}
            <br/>
            <span style="font-size: 0.75em;">
                Genom att ladda upp en fil intygar du att du har rätt att dela den och att innehållet är korrekt. <br>Läs mer i våra <a href="/legal#uppladdat-innehall-section" target="_blank">användarvillkor</a>.
            </span>
        </div>
        {/if}
    </div>
</div>
{/if}

<style>
	.upload-container {
		background-color: #f8f9fa;
		border-radius: 16px;
		padding: 30px;
		max-width: 500px;
		margin: 0 auto;
		box-shadow: 0 4px 12px rgba(0,0,0,0.08);
		border: 1px solid #e9ecef;
		box-sizing: border-box;
		text-align: center;
	}

	.header {
		text-align: center;
		margin-bottom: 25px;
	}

	.upload-icon {
		display: block;
		color: var(--hover);
		font-size: 2.5rem;
		margin-bottom: 10px;
	}

	.header h2 {
		font-size: 1.6rem;
		color: var(--box-title-color);
		margin: 0;
	}

	.upload-card {
		background-color: #ffffff;
		border-radius: 12px;
		padding: 20px;
		border: 1px solid #dee2e6;
	}

    .upload-steps {
        list-style: none;
        margin: 0 0 1rem 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 0;
    }
    .upload-steps .step {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.65rem 0;
        font-size: 0.9rem;
        color: #495057;
        border-bottom: 1px solid #eee;
    }
    .upload-steps .step:last-child {
        border-bottom: none;
    }
    .step-label {
        flex: 1;
        min-width: 0;
    }
    .step-status {
        flex-shrink: 0;
        width: 11rem;
        display: flex;
        align-items: center;
        justify-content: flex-end;
    }
    .step-icon {
        font-size: 1.1rem;
    }
    .step-done {
        color: #16a34a;
    }
    .step-pending {
        color: #94a3b8;
        margin-right: 0.35rem;
    }
    .step3-background {
        display: inline-flex;
        align-items: center;
        font-size: 0.8rem;
        color: #64748b;
        white-space: nowrap;
    }
    .action-button.open-brf-button {
        background-color: var(--hover);
        margin-bottom: 12px;
    }
    .action-button.open-brf-button:hover {
        background-color: #e85a4a;
    }

	.styled-file-input {
		display: none;
	}

    p.upload-instructions {
        font-size: 0.95rem;
        color: #495057;
        line-height: 1.6;
        margin-bottom: 20px;
        text-align: center;
    }

	.action-button.upload-trigger {
		background-color: #343a40;
		color: white;
		padding: 12px 25px;
		border: none;
		border-radius: 8px;
		cursor: pointer;
		font-weight: 600;
		transition: background-color 0.2s, transform 0.1s ease, box-shadow 0.2s ease;
		display: inline-block;
		width: fit-content;
		text-align: center;
		font-size: 1rem;
		box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
		margin-top: 10px;
		margin-bottom: 15px;
	}
	.action-button.upload-trigger:hover {
		background-color: #495057;
		transform: translateY(-2px);
		box-shadow: 0 5px 10px rgba(0, 0, 0, 0.15);
	}
	.action-button.upload-trigger:active {
		transform: translateY(0px);
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	}
    .action-button.upload-trigger .icon-prefix {
        margin-right: 8px;
    }

	.info-text {
		margin-top: 10px;
		font-size: 0.85rem;
		color: #6c757d;
		line-height: 1.5;
	}
	.info-text a {
		color: var(--hover);
		text-decoration: none;
	}
	.info-text a:hover {
		text-decoration: underline;
	}

    @media (max-width: 550px) {
        .upload-container {
            padding: 20px 15px;
            margin-left: 20px;
            margin-right: 20px;
        }
    }
</style>